import React, { Component, Fragment } from 'react';
import get from 'lodash/get';
import some from 'lodash/some';
import find from 'lodash/find';
import { LastLocationProvider } from 'react-router-last-location';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import LoadingBar, { hideLoading } from 'react-redux-loading-bar';
import Cookies from 'js-cookie';

import SEOHelmet from '../common/SEOHelmet';
import PlaceholderGenerator from '../../common/components/cms/PlaceholderGenerator';
import GraphQLClient from '../../common/utils/GraphQLClient';
import { handleRedirect, handleCountrySiteRedirect } from '../../common/utils';
import CountryContext from '../../common/context/country';
import LocaleContext from '../../common/context/locale';
import { checkSession as checkSessionAction } from '../../common/ducks/cart';
import { setIsAuthenticated as setIsAuthenticatedAction } from '../../common/ducks/auth';
import initI18n from '../../common/i18n/init';
import { checkLocale as checkLocaleAction } from '../../common/ducks/i18n';
import {
  Footer,
  HeaderMenu,
  Redirect
} from '../../common/components/navigation/';

// FIXME move to common
import { CookieConsentBar, RegionBar, NotificationBanner } from './components';
import ModalProvider from '../../common/components/modal/ModalProvider';
import ModalRoot from '../../common/components/modal/ModalRoot';
import { getContent } from '../../common/query';
import componentMap from './componentMap';
import { Drift } from './components';

import '../../common/styles/global.css';

let dateLocale = null;

class Page extends Component {
  static async getInitialProps({
    req,
    res,
    match,
    draft,
    location,
    allMessages,
    allLocales,
    newrelicHeader,
    isHeadRequest
  }) {
    let slug = draft
      ? match.params.slug
      : `${match.params.slug !== undefined ? `/${match.params.slug}` : ''}/`;

    const origin = req
      ? `https://${req.get('host')}`
      : document.location.origin;

    // Send the full path including query string to the graphql endpoint.
    if (req) {
      slug = req.url;
    }

    if (location) {
      // If there's no trailing slash, add one.
      if (!location.pathname.endsWith('/')) {
        const redirectLocation =
          location.pathname.replace(
            location.pathname,
            location.pathname + '/'
          ) +
          location.search +
          location.hash;
        return { redirectLocation };
      }

      slug = location.pathname + location.search;
    }

    const client = GraphQLClient(req, res);
    const pageData = await client.request(getContent, {
      path: slug,
      draft: draft || false
    });

    const { status, title: page, detectedLocale } = await pageData.getContent;

    const country = get(page, 'page.countrySite', {});
    const defaultLanguage = country.language;
    const defaultRegion = get(country, 'defaultCountry.iso', '').toLowerCase();

    const countries = get(page, 'page.countries', []);

    if (
      handleCountrySiteRedirect(req, res, detectedLocale, country, countries)
    ) {
      return null;
    }

    const redirectStatus = handleRedirect(res, status);
    if (redirectStatus) {
      return redirectStatus;
    }

    const language = get(page, 'page.countrySite.language', 'en');
    let messages;
    // Handle client side language switch
    if (process.env.BUILD_TARGET === 'client') {
      const i18n = initI18n();
      if (!i18n.hasResourceBundle(language, 'common') || !dateLocale) {
        const localeImport = await import(/* webpackChunkName: "locale-[request]" */ `../../common/i18n/locales/${language}.js`);
        messages = { common: localeImport.messages };
        dateLocale = localeImport.locale;
      }
    } else {
      messages = allMessages ? allMessages[language] : {};
      dateLocale =
        allLocales && allLocales[language] ? allLocales[language].locale : null;
    }

    return {
      slug,
      origin,
      page,
      messages,
      country,
      defaultLanguage,
      defaultRegion,
      countries,
      detectedLocale,
      newrelicHeader,
      isHeadRequest
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.location && nextProps.location) {
      const isSameLocation =
        this.props.location.pathname === nextProps.location.pathname &&
        this.props.location.search === nextProps.location.search;

      // region bar update must always trigger a update
      if (
        isSameLocation &&
        get(nextProps, 'location.state.forceUpdate', false)
      ) {
        return true;
      }
      if (isSameLocation) {
        return false;
      }
      if (
        nextProps.location.pathname.slice(0, 10) === '/dashboard' &&
        this.props.location.pathname.slice(0, 10) !== '/dashboard'
      ) {
        // Do a full page reload when entering Dashboard pages as a hack to force
        // the Dashboard js to initialize itself correctly and to clear CMS
        // components from previously visited pages from Dashboard pages.
        window.location.reload();
      }
    }
    return true;
  }

  componentDidUpdate() {
    this.runComponentMaintenance();
  }

  componentDidMount() {
    this.runComponentMaintenance();
  }

  runComponentMaintenance() {
    const { hideLoading, redirectLocation, setIsAuthenticated } = this.props;
    if (redirectLocation) {
      hideLoading();
      return;
    }

    const access_token = Cookies.get('flpc');
    if (access_token) {
      setIsAuthenticated(true);
    }
    hideLoading();
    this.fixStoreId();
  }

  async fixStoreId() {
    const {
      checkSession,
      checkLocale,
      defaultLanguage,
      defaultRegion,
      detectedLocale,
      country,
      countries
    } = this.props;

    // catching a race condition when defaultLanguage is undef after changing region (double update?)
    if (defaultLanguage) {
      const detectedRegionCode = get(detectedLocale, 'region.code', null);
      const region =
        detectedRegionCode &&
        some(country.allowedCountries, { iso: detectedRegionCode })
          ? detectedRegionCode.toLowerCase()
          : defaultRegion;

      // FIXME dont update if already set
      const currency = find(countries, { code: region.toUpperCase() })
        .currencyCode;

      await checkLocale(defaultLanguage, region, currency);
    }

    if (country) {
      checkSession(country.storePage);
    }
  }

  fixLanguage(language, messages) {
    const i18n = initI18n();
    if (!i18n.hasResourceBundle(language, 'common')) {
      i18n.addResourceBundle(language, 'common', messages.common);
    }
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }

  render() {
    const {
      origin,
      page,
      admin,
      redirectLocation,
      messages,
      country,
      countries,
      detectedLocale,
      defaultLanguage,
      defaultRegion,
      slug,
      newrelicHeader,
      isHeadRequest
    } = this.props;

    if (redirectLocation) {
      return <Redirect to={redirectLocation} />;
    }

    // Short circuit for HEAD request, response needs no body
    if (isHeadRequest) {
      return null;
    }

    if (!page) {
      return null;
    }

    this.fixLanguage(country.language, messages);

    const content = (
      <CountryContext.Provider value={{ ...country, allCountries: countries }}>
        <LastLocationProvider>
          <ModalProvider>
            <Fragment>
              {admin || (
                <SEOHelmet
                  seo={page.seo}
                  language={defaultLanguage}
                  region={defaultRegion}
                  origin={origin}
                />
              )}
              {admin ||
                (process.env.RAZZLE_SEGMENT_KEY && (
                  <Helmet>
                    <script type="text/javascript">{`
                    !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on"];analytics.factory=function(t){return function(){var e=Array.prototype.slice.call(arguments);e.unshift(t);analytics.push(e);return analytics}};for(var t=0;t<analytics.methods.length;t++){var e=analytics.methods[t];analytics[e]=analytics.factory(e)}analytics.load=function(t,e){var n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src="https://cdn.segment.com/analytics.js/v1/"+t+"/analytics.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(n,a);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.1.0";
                    analytics.load("${process.env.RAZZLE_SEGMENT_KEY}");
                    }}();
                  `}</script>
                    <script
                      type="text/javascript"
                      src="//cdn.bizible.com/scripts/bizible.js"
                      async
                    />
                  </Helmet>
                ))}

              {newrelicHeader ? (
                <Helmet>
                  <script type="text/javascript">{newrelicHeader}</script>
                </Helmet>
              ) : null}

              {page.page.countrySite.notificationBanner && (
                <NotificationBanner
                  startDate={page.page.countrySite.notificationBanner.startDate}
                  endDate={page.page.countrySite.notificationBanner.endDate}
                  message={page.page.countrySite.notificationBanner.message}
                />
              )}
              <div role="banner">
                <RegionBar
                  countries={countries}
                  detectedLocale={detectedLocale}
                  pageLanguage={defaultLanguage}
                  pageRegion={defaultRegion}
                  allowedCountries={country.allowedCountries}
                />
              </div>

              <div style={{ position: 'relative' }}>
                <LoadingBar
                  style={{
                    position: 'fixed',
                    top: 0,
                    width: '100%',
                    backgroundColor: '#0762c8',
                    height: '4px',
                    zIndex: 400000
                  }}
                  updateTime={100}
                  maxProgress={95}
                  progressIncrease={30}
                />

                {page.page.menu.length > 0 && (
                  <HeaderMenu
                    typeColor={
                      page.page.pageOptions.headerMenuColor
                        ? page.page.pageOptions.headerMenuColor
                        : 'black'
                    }
                    data={page.page.menu}
                    secondaryMenu={page.page.pageOptions.secondaryMenu}
                    additionalHeaderMenu={page.page.rightHeaderMenu}
                    countrySite={country}
                    slug={this.props.location}
                    countries={countries}
                  />
                )}
                <div className="content" role="main">
                  {page ? (
                    <PlaceholderGenerator
                      placeholders={page.page.placeholders}
                      admin={admin}
                      components={componentMap}
                    />
                  ) : (
                    ''
                  )}
                </div>
                {page.page.footerMenu.length > 0 && (
                  <Footer
                    menu={page.page.footerMenu}
                    countrySite={country}
                    slug={slug}
                    countries={countries}
                  />
                )}
              </div>
            </Fragment>
            <ModalRoot />
            <CookieConsentBar />
            <Drift appId="9923ruts8ypy" />
          </ModalProvider>
        </LastLocationProvider>
      </CountryContext.Provider>
    );

    if (dateLocale) {
      return (
        <LocaleContext.Provider value={{ dateLocale }}>
          {content}
        </LocaleContext.Provider>
      );
    }

    return content;
  }
}

export default connect(
  null,
  dispatch => {
    return {
      checkLocale: (language, region, currency) => {
        dispatch(checkLocaleAction(language, region, currency));
      },
      checkSession: storePage => {
        dispatch(checkSessionAction(storePage));
      },
      hideLoading: () => {
        dispatch(hideLoading());
      },
      setIsAuthenticated: isAuthenticated => {
        dispatch(setIsAuthenticatedAction(isAuthenticated));
      }
    };
  }
)(Page);
